import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cx } from "../cx";
import { Button } from "./Button";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface DialogProps {
  open: boolean;
  /** Visible title; also the dialog's accessible name. */
  title: ReactNode;
  onClose: () => void;
  children?: ReactNode;
  /** Buttons row at the bottom. */
  actions?: ReactNode;
  /** Hide the title visually (still the accessible name), e.g. the command palette. */
  hideTitle?: boolean;
  className?: string;
  /** Element to focus first; defaults to the first focusable element in the dialog. */
  initialFocusRef?: React.RefObject<HTMLElement>;
}

/**
 * Modal dialog: rendered in a portal, `aria-modal`, focus moves in and is trapped (Tab cycles), Escape and the scrim close
 * it, and focus returns to what had it before.
 */
export function Dialog({ open, title, onClose, children, actions, hideTitle, className, initialFocusRef }: DialogProps) {
  const titleId = useId();
  const boxRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const before = document.activeElement as HTMLElement | null;
    const box = boxRef.current;
    const first = initialFocusRef?.current ?? box?.querySelector<HTMLElement>(FOCUSABLE) ?? box;
    first?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        closeRef.current();
        return;
      }
      if (e.key !== "Tab" || !box) return;
      const items = Array.from(box.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    }
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      before?.focus?.();
    };
  }, [open, initialFocusRef]);

  if (!open) return null;
  return createPortal(
    <div className="lcc-root lcc-dialog">
      <div className="lcc-dialog__scrim" onClick={() => closeRef.current()} aria-hidden="true" />
      <div ref={boxRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} className={cx("lcc-dialog__box", className)}>
        <h2 id={titleId} className={cx("lcc-dialog__title", hideTitle && "lcc-sr-only")}>
          {title}
        </h2>
        {children !== undefined && <div className="lcc-dialog__body">{children}</div>}
        {actions && <div className="lcc-actions">{actions}</div>}
      </div>
    </div>,
    document.body,
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  title: ReactNode;
  /** What happens if they confirm, in plain words. */
  body?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** `danger` uses the quiet danger button (red text) for the confirm action. */
  tone?: "default" | "danger";
  busy?: boolean;
}

export function ConfirmDialog({ open, title, body, confirmLabel, cancelLabel = "Cancel", onConfirm, onCancel, tone = "default", busy }: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  return (
    <Dialog
      open={open}
      title={title}
      onClose={onCancel}
      initialFocusRef={cancelRef}
      actions={
        <>
          <Button variant={tone === "danger" ? "danger-quiet" : "primary"} size="lg" block onClick={onConfirm} disabled={busy}>
            {confirmLabel}
          </Button>
          <Button ref={cancelRef} variant="secondary" size="lg" onClick={onCancel}>
            {cancelLabel}
          </Button>
        </>
      }
    >
      {body}
    </Dialog>
  );
}
